import { useEffect, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Navigate, Outlet, Route, Routes, useHref, useLocation, useNavigate } from 'react-router';
import { RouterProvider } from 'react-aria-components';
import {
  accessToken,
  currentRole,
  passwordChangeRequired,
  SESSION_EXPIRED_EVENT,
  signIn,
} from './auth';
import { canAccess, type AdminCapability } from './auth-policy';
import { ChangePassword, ForgotPassword, ResetPassword } from './AuthRoutes';
import { Button } from '@ui/components/base/buttons/button';
import { Callout } from '@ui/console/primitives';
import { FormCard, TextField } from '@ui/console/forms';
import { Actions, Page } from './components';
import { PasswordField } from './PasswordField';
import { ActivityLogs, Dashboard, NotFound, PushDelivery } from './operations';
import { GarmentQualityCheck } from './garment-quality-check';
import { AccountDeletions } from './account-restore';
import { AiCosts } from './ai-costs';
import { AuditLog } from './audit-log';
import { EmailDeliveries } from './email-delivery';
import { OperatorControls } from './operator-controls';
import { UsageLimitDefaults } from './usage-limit-defaults';
import { FeatureFlags } from './feature-flags';
import { ProductMetrics } from './product-metrics';
import { GarmentData } from './garment-data';
import { MobileTelemetry } from './mobile-telemetry';
import { EnrichmentFailures } from './consumer-insights';
import {
  Accounts,
  ConsumerCreate,
  ConsumerDetails,
  ConsumerList,
  StylistCreate,
  StylistDetails,
  StylistList,
  Waitlist,
} from './people';
import { Shell } from './Shell';
import { WorkflowJobDetail, WorkflowJobs } from './workflow-jobs';
import { RecentSearches } from './search-history';
import { MemberFeedback } from './feedback';
import { WardrobeImportDetail } from './wardrobe-import';
import { StylistAssignments } from './stylist-assignments';

export function UsageLimits() {
  return (
    <Page
      title="Fleet-wide usage limit defaults"
      description="Set the defaults every member inherits when no per-member override exists. Members with explicit overrides keep those limits."
    >
      <UsageLimitDefaults />
    </Page>
  );
}

function RequireAuth() {
  const location = useLocation();
  const navigate = useNavigate();
  const [session, setSession] = useState<'live' | 'absent' | 'expired'>(() =>
    accessToken() ? 'live' : 'absent',
  );
  useEffect(() => {
    const expire = () => {
      setSession('expired');
      void navigate('/sign-in', { replace: true, state: { from: location, expired: true } });
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, expire);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, expire);
  }, [location, navigate]);
  if (session !== 'live') {
    // Whichever of this and the navigate above lands first, the sign-in page
    // still says why the operator is there.
    return (
      <Navigate to="/sign-in" state={{ from: location, expired: session === 'expired' }} replace />
    );
  }
  if (passwordChangeRequired() && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  return <Outlet />;
}

function AccessDenied() {
  return (
    <Page
      title="Access restricted"
      description="This screen exists, but your role is not allowed to open it. Nothing has gone wrong and you are still signed in."
    >
      <Callout tone="error" title="Your role has not been granted this screen.">
        Ask an admin to widen your access if you need it. Everything else in the menu still works.
      </Callout>
    </Page>
  );
}

function RequireCapability({
  capability,
  children,
}: {
  capability: AdminCapability;
  children: ReactNode;
}) {
  return canAccess(currentRole(), capability) ? children : <AccessDenied />;
}

// The Dashboard reads /api/v1/internal/ routes, which answer only the admin
// workspace roles. Everyone else is told so instead of shown a page of failed
// reads.
function Home() {
  return canAccess(currentRole(), 'admin:workspace') ? <Dashboard /> : <AccessDenied />;
}

function RequirePrivilegedOperator({ children }: { children: ReactNode }) {
  return <RequireCapability capability="admin:privileged-operator">{children}</RequireCapability>;
}

function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const expired = Boolean((location.state as { expired?: boolean } | null)?.expired);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const email = form.get('email');
    const password = form.get('password');
    setLoading(true);
    setError(undefined);
    try {
      const mustChangePassword = await signIn(
        typeof email === 'string' ? email : '',
        typeof password === 'string' ? password : '',
      );
      if (mustChangePassword) {
        void navigate('/change-password', { replace: true });
        return;
      }
      const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
      void navigate(from && from !== '/sign-in' ? from : '/', { replace: true });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Sign-in failed.');
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="auth-page">
      <Page
        title="Admin sign in"
        description="Sign in with your own staff account. What you can see and do here depends on the role that account was given."
      >
        <FormCard title="Operator sign in" onSubmit={(event) => void submit(event)}>
          <TextField label="Email" name="email" type="email" autoComplete="username" required />
          <PasswordField
            label="Password"
            name="password"
            autoComplete="current-password"
            required
          />
          {error ? (
            <Callout tone="error" title={error} />
          ) : expired ? (
            <Callout tone="warning" title="Your session expired. Sign in again to carry on." />
          ) : null}
          <Actions>
            <Button size="md" color="link-gray" href="/forgot-password">
              Forgot password?
            </Button>
            <Button size="md" color="primary" type="submit" isDisabled={loading}>
              {loading ? 'Signing in…' : 'Sign in'}
            </Button>
          </Actions>
        </FormCard>
      </Page>
    </div>
  );
}

/**
 * React Aria renders a `Button href` as its own link, which would do a full
 * page load without this bridge to the router.
 */
function AriaRouting({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  return (
    <RouterProvider navigate={(to) => void navigate(to)} useHref={useHref}>
      {children}
    </RouterProvider>
  );
}

export function App() {
  return (
    <AriaRouting>
      <Routes>
        <Route path="/sign-in" element={<SignIn />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route element={<RequireAuth />}>
          <Route element={<Shell />}>
            <Route index element={<Home />} />
            <Route
              element={
                <RequireCapability capability="admin:workspace">{<Outlet />}</RequireCapability>
              }
            >
              <Route path="waitlist" element={<Waitlist />} />
              <Route path="accounts" element={<Accounts />} />
              <Route path="consumers" element={<ConsumerList />} />
              <Route path="consumers/new" element={<ConsumerCreate />} />
              <Route path="consumers/:id" element={<ConsumerDetails />} />
              <Route path="wardrobe-imports/:importId" element={<WardrobeImportDetail />} />
              <Route path="account-deletions" element={<AccountDeletions />} />
              <Route path="stylists" element={<StylistList />} />
              <Route path="stylists/new" element={<StylistCreate />} />
              <Route path="stylists/:id" element={<StylistDetails />} />
              <Route path="member-scheduling" element={<Navigate to="/consumers" replace />} />
              <Route path="stylist-assignments" element={<StylistAssignments />} />
              <Route
                path="stylist-routing"
                element={<Navigate to="/stylist-assignments" replace />}
              />
              <Route path="garment-quality-check" element={<GarmentQualityCheck />} />
              {/* Garment review & publishing treated review as a gate before
                  the member saw anything. It is a quality check after the
                  fact now; the old URL lands on it. */}
              <Route
                path="wardrobe-review"
                element={<Navigate to="/garment-quality-check" replace />}
              />
              <Route path="enrichment-failures" element={<EnrichmentFailures />} />
              <Route path="daily-look-batches" element={<Navigate to="/" replace />} />
              {/* The three screens that merged into Member product records,
                  and Member product records itself: it was a label for an
                  arbitrary grouping of five tables, four of which nothing
                  writes. What is left reads one table, and says so. Old
                  links, and anything bookmarked, still land somewhere. */}
              <Route path="product-records" element={<Navigate to="/" replace />} />
              <Route path="looks-recommendations" element={<Navigate to="/" replace />} />
              <Route path="profiles-body-data" element={<Navigate to="/" replace />} />
              <Route path="curated-boxes" element={<Navigate to="/" replace />} />
              {/* Failed tasks & controls had no subject of its own: three of
                  its four tiles are on the screens that own them and its list
                  read `task_executions`, which nothing writes. The queue it
                  claimed to show is Workflow jobs, filtered. */}
              <Route
                path="pilot-controls"
                element={<Navigate to="/workflow-jobs?state=dead_lettered" replace />}
              />
              <Route
                path="email-deliveries"
                element={
                  <RequirePrivilegedOperator>
                    <EmailDeliveries />
                  </RequirePrivilegedOperator>
                }
              />
              <Route
                path="push-delivery"
                element={
                  <RequirePrivilegedOperator>
                    <PushDelivery />
                  </RequirePrivilegedOperator>
                }
              />
              <Route path="workflow-jobs" element={<WorkflowJobs />} />
              <Route path="workflow-jobs/:jobId" element={<WorkflowJobDetail />} />
              <Route path="product-metrics" element={<ProductMetrics />} />
              <Route path="garment-data" element={<GarmentData />} />
              <Route path="searches" element={<RecentSearches />} />
              <Route path="feedback" element={<MemberFeedback />} />
              <Route path="audit-log" element={<AuditLog />} />
              <Route path="activity-logs" element={<ActivityLogs />} />
              <Route path="mobile-errors" element={<MobileTelemetry />} />
              <Route path="ai-costs" element={<AiCosts />} />
              <Route
                path="usage-limits"
                element={
                  <RequirePrivilegedOperator>
                    <UsageLimits />
                  </RequirePrivilegedOperator>
                }
              />
              {/* Managers read flags; the screen itself withholds changes from
                  every role without configuration:manage. */}
              <Route path="feature-flags" element={<FeatureFlags />} />
              <Route
                path="operator-controls"
                element={
                  <RequirePrivilegedOperator>
                    <OperatorControls />
                  </RequirePrivilegedOperator>
                }
              />
            </Route>
            <Route path="change-password" element={<ChangePassword />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Route>
      </Routes>
    </AriaRouting>
  );
}
